import { useEffect, useRef } from "react";
import { YAMLParseError, parse, stringify } from "yaml";

import { fps } from "~/tools/fps";
import { useScoped, useScopedEffect } from "~/tools/impulse";
import { ImpulseFormUnit } from "~/tools/impulse-form";
import { onTimeout } from "~/tools/on-timeout";
import { tryCatch } from "~/tools/try-catch";
import { usePermanent } from "~/tools/use-permanent";

import type { FormState } from "../_form-state";

import { formToUnknown } from "./_form-to-unknown";
import { unknownToForm } from "./_unknown-to-form";

/**
 * Custom React hook for managing a YAML-backed form unit with validation and two-way synchronization
 * between a YAML editor and a form state.
 * The YAML editor form state is detached from the main form state because it derives from it.
 *
 * This hook creates an `ImpulseFormUnit` that holds the YAML string, validates it using a YAML parser,
 * and synchronizes changes bidirectionally between the YAML input and the provided form state.
 *
 * - When the YAML changes, the form state is updated accordingly.
 * - When the form state changes, the YAML input is updated, with debouncing to prevent flickering.
 * - Validation is performed immediately on initialization and whenever the YAML input changes.
 *
 * @param form - The form state object to synchronize with the YAML input.
 * @returns An `ImpulseFormUnit` instance managing the YAML string, validation errors, and parsed output.
 */
export function useYamlForm(form: FormState): ImpulseFormUnit<string, null | YAMLParseError, unknown> {
	/**
	 * The ref prevents the editor content from being synced during editing.
	 * It cannot be state to avoid the effect triggering.
	 * The initial value is `true` to prevent the initial effect from running.
	 */
	const isUpdatingRef = useRef(true);
	const yaml = useScoped((scope) => stringify(formToUnknown(scope, form), null, 2), [form]);

	const unit = usePermanent(() => {
		return ImpulseFormUnit(yaml, {
			validateOn: "onInit", // validate immediately
			validate: (input) => {
				const [error, result] = tryCatch((): unknown => parse(input));

				if (error instanceof YAMLParseError) {
					return [error, null];
				}

				return [null, result];
			},
		});
	});

	// update the form state when the YAML changes
	useScopedEffect(
		(scope) => {
			isUpdatingRef.current = true;
			unknownToForm(form, unit.getOutput(scope));
		},
		[unit, form],
	);

	// update the YAML input when the form changes
	useEffect(() => {
		// debounce the update to avoid editor flickering
		return onTimeout(() => {
			// do not update the unit state if the change is triggered by the unit itself
			if (!isUpdatingRef.current) {
				unit.setInput(yaml);
			}

			isUpdatingRef.current = false;
		}, fps(3));
	}, [unit, yaml]);

	return unit;
}
